import type { ForwardedRef, KeyboardEvent, RefCallback } from 'react';
import { forwardRef, useCallback, useEffect, useRef } from 'react';

import { classNames } from '../../../../utils';
import { focusFirstFocusableNode, handleMouseUpByBlurring } from '../../../../utils/focus';
import styles from '../STabs.module.css';
import type { BaseSTabProps } from '../types';

export const STab = forwardRef(
    (
        {
            content,
            accessibilityLabel,
            badge,
            id,
            panelID,
            url,
            onAction,
            disabled,
            icon,
            siblingTabHasFocus,
            measuring,
            focused,
            selected,
            tabIndexOverride,
            onFocus,
        }: BaseSTabProps,
        ref: ForwardedRef<HTMLElement>
    ) => {
        const wasSelected = useRef(selected);
        const panelFocused = useRef(false);
        const node = useRef<HTMLLIElement | null>(null);

        // A tab can start selected when it is moved from the disclosure dropdown into the main list, so we need to send focus from the tab to the panel on mount and update
        useEffect(() => {
            if (measuring) {
                return;
            }

            // Because of timing issues with the render, we may still have the old, in-disclosure version of the tab that has focus. Check for this as a second indicator of focus
            const itemHadFocus = focused || (document.activeElement && document.activeElement.id === id);

            // If we just check for selected, the panel for the active tab will be focused on page load, which we don’t want
            if (itemHadFocus && selected && panelID != null && !panelFocused.current) {
                focusPanelID(panelID);
                panelFocused.current = true;
            }

            if (selected && !wasSelected.current && panelID != null) {
                focusPanelID(panelID);
            } else if (focused && node.current != null && !disabled) {
                focusFirstFocusableNode(node.current);
            }

            wasSelected.current = selected;
        }, [focused, id, content, measuring, panelID, selected, disabled]);

        let tabIndex: 0 | -1;

        if (selected && !siblingTabHasFocus && !measuring) {
            tabIndex = 0;
        } else if (focused && !measuring) {
            tabIndex = 0;
        } else {
            tabIndex = -1;
        }

        if (tabIndexOverride != null) {
            tabIndex = tabIndexOverride;
        }

        const handleClick = useCallback(() => {
            if (disabled) {
                return;
            }
            if (!selected) {
                onAction?.();
            }
        }, [selected, onAction, disabled]);

        const handleKeyDown = useCallback(
            (event: KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>) => {
                if (event.key === ' ') {
                    event.preventDefault();
                    handleClick();
                }
            },
            [handleClick]
        );

        const tabContainerClassNames = classNames(styles.TabContainer, selected && styles.Underline);

        const urlIfNotDisabledOrSelected = disabled || selected ? undefined : url;

        const BaseComponent = urlIfNotDisabledOrSelected ? 'a' : 'button';

        const tabClassName = classNames(styles.Tab, icon && styles['Tab-iconOnly'], selected && styles['Tab-active']);

        const badgeMarkup = badge ? <s-badge tone={selected ? undefined : 'info'}>{badge}</s-badge> : null;

        const activator = (
            <BaseComponent
                id={id}
                className={tabClassName}
                tabIndex={tabIndex}
                aria-selected={selected}
                aria-controls={panelID}
                aria-label={accessibilityLabel}
                role={tabIndexOverride == null ? 'tab' : undefined}
                disabled={disabled}
                href={urlIfNotDisabledOrSelected}
                onFocus={onFocus}
                onMouseUp={handleMouseUpByBlurring}
                onClick={handleClick}
                onKeyDown={handleKeyDown}
            >
                <div className="flex items-center gap-2 flex-nowrap justify-center">
                    <span className="text-body-sm font-medium">{icon ?? content}</span>
                    {badgeMarkup}
                </div>
            </BaseComponent>
        );

        return (
            <li className={tabContainerClassNames} ref={mergeRefs([node, ref])} role="presentation">
                {activator}
            </li>
        );
    }
);

STab.displayName = 'STab';

function focusPanelID(panelID: string) {
    const panel = document.getElementById(panelID);
    if (panel) {
        panel.focus({ preventScroll: true });
    }
}

function mergeRefs<T = any>(refs: ForwardedRef<T>[]): RefCallback<T> {
    return (node) => {
        refs.forEach((ref) => {
            if (typeof ref === 'function') {
                ref(node);
            } else if (ref != null) {
                ref.current = node;
            }
        });
    };
}
